// 058 — Cuidados Paliativos (Fase 5, Tanda 6, ver PLAN.md). Transcripción campo
// por campo del PDF oficial (páginas 209-210, footer `SNS-MSP/HCU-form.058/2021`,
// 2 hojas, bloques A-R). Las 6 escalas que resume el bloque M (KPS/PPS,
// Barthel, Pfeiffer, DME, Gijón, Zarit) se completan en `058A` (Tanda 7): acá M
// solo registra el resultado de cada una, tal como el papel.
//
// Criterios de siempre: equivalencia funcional (no se dibuja la hoja: las
// siluetas del examen físico y el familiograma gráfico quedan como texto), nada
// de contenido clínico que el papel no imprime (las abreviaturas de estado civil
// van tal cual están impresas), y FHIR solo con consumidor real: `Observation`
// de signos vitales (alimentan la pestaña Signos Vitales) y un `Condition` por
// diagnóstico — el resto queda como `Par[]` de respaldo en el PDF firmado.
import { diagnosticoConMarcaVacio, textoDiagnosticoConMarca, type DiagnosticoConMarca } from './MarcaDiagnostico';
import {
  OPCIONES_SI_NO,
  MARCAS_PRE_DEF,
  paresDeCampos,
  valoresVacios,
  type CampoSimple,
  type Valores,
} from './camposSimplesDatos';
import type { Par } from './index';
import type { Traducir } from './tecnicaAnestesicaDatos';

const casilla = (clave: string, etiqueta: string): CampoSimple => ({ clave, etiqueta, tipo: 'casilla' });
const numero = (clave: string, etiqueta: string): CampoSimple => ({ clave, etiqueta, tipo: 'numero' });
const siNo = (clave: string, etiqueta: string): CampoSimple => ({ clave, etiqueta, tipo: 'select', opciones: OPCIONES_SI_NO });

export const NUM_DIAGNOSTICOS_058 = 6;

// ---------- B. Datos personales (nombre, sexo, fecha de nacimiento y edad salen del encabezado MSP) ----------

export const CAMPOS_PERSONALES_058: readonly CampoSimple[] = [
  {
    clave: 'tipoDocumento',
    etiqueta: 'Tipo de documento de identificación',
    tipo: 'select',
    opciones: [
      { valor: 'ccci', etiqueta: 'CC/CI' },
      { valor: 'pas', etiqueta: 'PAS' },
      { valor: 'carne', etiqueta: 'Carné' },
      { valor: 'sd', etiqueta: 'S/D' },
    ],
  },
  {
    clave: 'estadoCivil',
    etiqueta: 'Estado civil',
    tipo: 'select',
    opciones: ['SOL', 'CAS', 'DIV', 'VIU', 'UN', 'U-H', 'NA'].map((a) => ({ valor: a.toLowerCase(), etiqueta: a })),
  },
  { clave: 'telefonoFijo', etiqueta: 'N.º de teléfono fijo' },
  { clave: 'telefonoCelular', etiqueta: 'N.º de teléfono celular' },
  { clave: 'correo', etiqueta: 'Correo electrónico' },
  { clave: 'lugarNacimiento', etiqueta: 'Lugar de nacimiento' },
  { clave: 'nacionalidad', etiqueta: 'Nacionalidad' },
  { clave: 'provincia', etiqueta: 'Residencia habitual — provincia' },
  { clave: 'canton', etiqueta: 'Residencia habitual — cantón' },
  { clave: 'parroquia', etiqueta: 'Residencia habitual — parroquia' },
  { clave: 'barrioSector', etiqueta: 'Residencia habitual — barrio o sector' },
  { clave: 'callePrincipal', etiqueta: 'Residencia habitual — calle principal' },
  { clave: 'calleSecundaria', etiqueta: 'Residencia habitual — calle secundaria' },
  { clave: 'referencia', etiqueta: 'Residencia habitual — referencia' },
];

// ---------- C. Cuidador/a principal ----------

export const CAMPOS_CUIDADOR_058: readonly CampoSimple[] = [
  { clave: 'nombre', etiqueta: 'Nombre' },
  { clave: 'direccion', etiqueta: 'Dirección' },
  { clave: 'telefono', etiqueta: 'N.º de teléfono' },
  { clave: 'correo', etiqueta: 'Correo electrónico' },
  // El papel imprime 6 recuadros de parentesco + "otro": es una sola respuesta por cuidador, no varias marcas.
  {
    clave: 'parentesco',
    etiqueta: 'Parentesco',
    tipo: 'select',
    opciones: [
      { valor: 'hijo', etiqueta: 'Hijo/a' },
      { valor: 'conyuge', etiqueta: 'Cónyuge' },
      { valor: 'padreMadre', etiqueta: 'Padre / madre' },
      { valor: 'nieto', etiqueta: 'Nieto' },
      { valor: 'yernoNuera', etiqueta: 'Yerno / nuera' },
      { valor: 'noTiene', etiqueta: 'No tiene' },
      { valor: 'otro', etiqueta: 'Otro (especifique)' },
    ],
  },
  { clave: 'parentescoOtro', etiqueta: 'Parentesco — otro (especifique)' },
];

// ---------- D / O. Diagnósticos ----------

export const CAMPOS_PATOLOGIA_INGRESO_058: readonly CampoSimple[] = [
  {
    clave: 'tipo',
    etiqueta: 'Patología por la que ingresa a cuidados paliativos',
    tipo: 'select',
    opciones: [
      { valor: 'oncologico', etiqueta: 'Oncológico' },
      { valor: 'noOncologico', etiqueta: 'No oncológico' },
    ],
  },
];

// ---------- E / F ----------

export const CAMPOS_MOTIVO_ENFERMEDAD_058: readonly CampoSimple[] = [
  { clave: 'motivoConsulta', etiqueta: 'E. Motivo de consulta', tipo: 'area' },
  { clave: 'enfermedadActual', etiqueta: 'F. Enfermedad o problema actual', tipo: 'area' },
];

// ---------- G / H ----------

export const CAMPOS_ANTECEDENTES_058: readonly CampoSimple[] = [
  casilla('cardiopatia', '1. Cardiopatía'),
  casilla('hipertension', '2. Hipertensión'),
  casilla('enfCVascular', '3. Enf. C. vascular'),
  casilla('endocrinoMetabolico', '4. Endócrino metabólico'),
  casilla('cancer', '5. Cáncer'),
  casilla('tuberculosis', '6. Tuberculosis'),
  casilla('enfMental', '7. Enf. mental'),
  casilla('enfInfecciosa', '8. Enf. infecciosa'),
  casilla('malformacion', '9. Malformación'),
  casilla('otro', '10. Otro'),
  { clave: 'descripcion', etiqueta: 'Datos clínico-quirúrgicos, obstétricos, alérgicos relevantes', tipo: 'area' },
];

export const CAMPOS_HABITOS_058: readonly CampoSimple[] = [
  casilla('alcohol', '1. Alcohol'),
  casilla('tabaco', '2. Tabaco'),
  casilla('drogas', '3. Drogas'),
  casilla('aficiones', '4. Aficiones'),
  casilla('caracter', '5. Carácter'),
  casilla('creencias', '6. Creencias'),
  casilla('valores', '7. Valores'),
  casilla('alimentos', '8. Alimentos'),
  casilla('medicamentos', '9. Medicamentos'),
  casilla('miccional', '10. Miccional'),
  casilla('defecatorios', '11. Defecatorios'),
  casilla('actividadFisica', '12. Actividad física'),
  casilla('otros', '13. Otros'),
  { clave: 'descripcion', etiqueta: 'Descripción', tipo: 'area' },
];

// ---------- I. Conocimiento de la enfermedad ----------

export const CAMPOS_CONOCIMIENTO_058: readonly CampoSimple[] = [
  siNo('pacienteDiagnostico', 'El paciente — conoce el diagnóstico'),
  siNo('pacientePronostico', 'El paciente — conoce el pronóstico'),
  siNo('voluntadAnticipada', 'El paciente — voluntad anticipada'),
  siNo('testamentoVital', 'El paciente — testamento vital'),
  siNo('familiarDiagnosticoPronostico', 'El familiar / cuidador principal — conoce el diagnóstico y pronóstico'),
];

// ---------- J. Constantes vitales y antropometría ----------

/** La presión arterial impresa es un solo campo (mmHg); se captura sistólica/diastólica para poder crear la `Observation` de panel de presión arterial. */
export const CAMPOS_CONSTANTES_058: readonly CampoSimple[] = [
  numero('temperatura', 'Temperatura (°C)'),
  numero('paSistolica', 'Presión arterial sistólica (mmHg)'),
  numero('paDiastolica', 'Presión arterial diastólica (mmHg)'),
  numero('pulso', 'Pulso / min'),
  numero('frecuenciaRespiratoria', 'Frecuencia respiratoria / min'),
  numero('peso', 'Peso (kg)'),
  numero('talla', 'Talla (cm)'),
  numero('pulsioximetria', 'Pulsioximetría (%)'),
];

function numeroValido(texto: string | undefined): number | undefined {
  if (texto === undefined || texto.trim() === '') return undefined;
  const n = Number(texto);
  return Number.isNaN(n) ? undefined : n;
}

/** IMC (kg/m²) calculado, nunca tipeado — `undefined` si falta peso o talla, o la talla no es positiva. */
export function imc058(constantes: Valores): number | undefined {
  const peso = numeroValido(constantes.peso);
  const talla = numeroValido(constantes.talla);
  if (peso === undefined || talla === undefined || talla <= 0) return undefined;
  return peso / ((talla / 100) * (talla / 100));
}

// ---------- K / L / M / N ----------

export const CAMPOS_EXAMEN_FISICO_058: readonly CampoSimple[] = [
  { clave: 'examenFisico', etiqueta: 'K. Examen físico (hallazgos; el diagrama corporal del papel se describe en texto)', tipo: 'area' },
];

const sintoma = (clave: string, etiqueta: string): CampoSimple => ({ ...numero(clave, etiqueta), min: 0, max: 10 });

/** L — cada síntoma se puntúa de 0 a 10 (campo libre por síntoma en el papel, sin fórmula agregada). */
export const CAMPOS_SINTOMAS_058: readonly CampoSimple[] = [
  { clave: 'fecha', etiqueta: 'Fecha de evaluación', tipo: 'fecha' },
  sintoma('dolor', 'Dolor (0-10)'),
  sintoma('cansancio', 'Cansancio (0-10)'),
  sintoma('nausea', 'Náusea (0-10)'),
  sintoma('depresion', 'Depresión (0-10)'),
  sintoma('ansiedad', 'Ansiedad (0-10)'),
  sintoma('somnolencia', 'Somnolencia (0-10)'),
  sintoma('hiporexia', 'Hiporexia (0-10)'),
  sintoma('disnea', 'Disnea (0-10)'),
  sintoma('dificultadDormir', 'Dificultad para dormir (0-10)'),
  sintoma('estrenimiento', 'Estreñimiento (0-10)'),
];

/** Síntomas de L que se puntúan (todos menos la fecha). */
const CLAVES_PUNTUABLES_058 = CAMPOS_SINTOMAS_058.filter((c) => c.tipo === 'numero');

/**
 * L puntúa cada síntoma de 0 a 10: devuelve las etiquetas de los que tienen un valor que no es un entero de 0 a 10
 * (vacío = sin evaluar, válido). Quien guarda debe rechazar el guardado si hay alguno.
 */
export function sintomasFueraDeRango058(v: Valores): string[] {
  return CLAVES_PUNTUABLES_058.filter((c) => {
    const texto = (v[c.clave] ?? '').trim();
    return texto !== '' && !/^(10|[0-9])$/.test(texto);
  }).map((c) => c.etiqueta);
}

/** M — resultado de cada escala (el detalle vive en 058A). */
export const CAMPOS_COMPONENTES_058: readonly CampoSimple[] = [
  { clave: 'fecha', etiqueta: 'Fecha de evaluación', tipo: 'fecha' },
  { clave: 'kpsPps', etiqueta: 'KPS y PPS' },
  { clave: 'barthel', etiqueta: 'Barthel' },
  { clave: 'pfeiffer', etiqueta: 'Pfeiffer' },
  { clave: 'dme', etiqueta: 'DME' },
  { clave: 'gijon', etiqueta: 'Gijón' },
  { clave: 'zarit', etiqueta: 'Zarit' },
  { clave: 'necEspiritual', etiqueta: 'Nec. espiritual' },
  { clave: 'observaciones', etiqueta: 'Observaciones', tipo: 'area' },
];

export const CAMPOS_FAMILIOGRAMA_058: readonly CampoSimple[] = [
  { clave: 'familiograma', etiqueta: 'N. Familiograma (el espacio gráfico del papel se describe en texto)', tipo: 'area' },
];

// ---------- P / Q / R ----------

export const CAMPOS_PLAN_058: readonly CampoSimple[] = [
  { clave: 'plan', etiqueta: 'P. Plan de tratamiento (diagnóstico, terapéutico y educacional)', tipo: 'area' },
  casilla('opioideInicia', 'Prescripción de opioide potente — inicia'),
  casilla('opioideContinua', 'Prescripción de opioide potente — continúa'),
];

export const CAMPOS_INFORMANTE_058: readonly CampoSimple[] = [
  { clave: 'nombre', etiqueta: 'Nombre' },
  { clave: 'parentesco', etiqueta: 'Parentesco' },
];

// ---------- Estado ----------

export interface EstadoBitacora058 {
  readonly personales: Valores;
  readonly cuidador: Valores;
  readonly diagnosticosIngreso: readonly DiagnosticoConMarca[];
  readonly patologiaIngreso: Valores;
  readonly motivoEnfermedad: Valores;
  readonly antecedentes: Valores;
  readonly habitos: Valores;
  readonly conocimiento: Valores;
  readonly constantes: Valores;
  readonly examenFisico: Valores;
  readonly sintomas: Valores;
  readonly componentes: Valores;
  readonly familiograma: Valores;
  readonly diagnosticos: readonly DiagnosticoConMarca[];
  readonly plan: Valores;
  readonly informante: Valores;
}

const diagnosticosVacios = (): DiagnosticoConMarca[] => Array.from({ length: NUM_DIAGNOSTICOS_058 }, () => diagnosticoConMarcaVacio());

export function estadoInicialBitacora058(): EstadoBitacora058 {
  return {
    personales: valoresVacios(CAMPOS_PERSONALES_058),
    cuidador: valoresVacios(CAMPOS_CUIDADOR_058),
    diagnosticosIngreso: diagnosticosVacios(),
    patologiaIngreso: valoresVacios(CAMPOS_PATOLOGIA_INGRESO_058),
    motivoEnfermedad: valoresVacios(CAMPOS_MOTIVO_ENFERMEDAD_058),
    antecedentes: valoresVacios(CAMPOS_ANTECEDENTES_058),
    habitos: valoresVacios(CAMPOS_HABITOS_058),
    conocimiento: valoresVacios(CAMPOS_CONOCIMIENTO_058),
    constantes: valoresVacios(CAMPOS_CONSTANTES_058),
    examenFisico: valoresVacios(CAMPOS_EXAMEN_FISICO_058),
    sintomas: valoresVacios(CAMPOS_SINTOMAS_058),
    componentes: valoresVacios(CAMPOS_COMPONENTES_058),
    familiograma: valoresVacios(CAMPOS_FAMILIOGRAMA_058),
    diagnosticos: diagnosticosVacios(),
    plan: valoresVacios(CAMPOS_PLAN_058),
    informante: valoresVacios(CAMPOS_INFORMANTE_058),
  };
}

function paresDeDiagnosticos(t: Traducir, clave: string, diagnosticos: readonly DiagnosticoConMarca[]): Par[] {
  return diagnosticos.map((d, i) => ({
    pregunta: `${t(clave)} — ${i + 1}`,
    respuesta: textoDiagnosticoConMarca(d, MARCAS_PRE_DEF),
  }));
}

/** Serializa toda la historia de 058 a `Par[]`, listos para `onGuardado` — filtra respuestas vacías. */
export function paresDeBitacora058(t: Traducir, estado: EstadoBitacora058): Par[] {
  // Se guarda lo recortado; el "otro" del parentesco solo vale si el parentesco elegido es "otro".
  const recortar = (v: Valores): Valores => Object.fromEntries(Object.entries(v).map(([k, x]) => [k, x.trim()]));
  const e: EstadoBitacora058 = {
    ...estado,
    sintomas: recortar(estado.sintomas),
    cuidador: { ...estado.cuidador, parentescoOtro: estado.cuidador.parentesco === 'otro' ? (estado.cuidador.parentescoOtro ?? '').trim() : '' },
  };
  const p = (clave: string, campos: readonly CampoSimple[], valores: Valores): Par[] => paresDeCampos(campos, valores, `${t(`formularios.f058.${clave}`)} — `);
  const imc = imc058(e.constantes);
  const pares: Par[] = [
    ...p('personalesTitulo', CAMPOS_PERSONALES_058, e.personales),
    ...p('cuidadorTitulo', CAMPOS_CUIDADOR_058, e.cuidador),
    ...paresDeDiagnosticos(t, 'formularios.f058.diagnosticoIngresoTitulo', e.diagnosticosIngreso),
    ...paresDeCampos(CAMPOS_PATOLOGIA_INGRESO_058, e.patologiaIngreso),
    ...paresDeCampos(CAMPOS_MOTIVO_ENFERMEDAD_058, e.motivoEnfermedad),
    ...p('antecedentesTitulo', CAMPOS_ANTECEDENTES_058, e.antecedentes),
    ...p('habitosTitulo', CAMPOS_HABITOS_058, e.habitos),
    ...p('conocimientoTitulo', CAMPOS_CONOCIMIENTO_058, e.conocimiento),
    ...p('constantesTitulo', CAMPOS_CONSTANTES_058, e.constantes),
    { pregunta: `${t('formularios.f058.constantesTitulo')} — IMC (kg/m²)`, respuesta: imc === undefined ? '' : imc.toFixed(1) },
    ...paresDeCampos(CAMPOS_EXAMEN_FISICO_058, e.examenFisico),
    ...p('sintomasTitulo', CAMPOS_SINTOMAS_058, e.sintomas),
    ...p('componentesTitulo', CAMPOS_COMPONENTES_058, e.componentes),
    ...paresDeCampos(CAMPOS_FAMILIOGRAMA_058, e.familiograma),
    ...paresDeDiagnosticos(t, 'formularios.f058.diagnosticoTitulo', e.diagnosticos),
    ...paresDeCampos(CAMPOS_PLAN_058, e.plan),
    ...p('informanteTitulo', CAMPOS_INFORMANTE_058, e.informante),
  ];
  return pares.filter((x) => x.respuesta.trim() !== '');
}
